import { useState, useEffect } from 'react';
import { supabase } from './supabase';

// ─── Constants ────────────────────────────────────────────────
const SOURCES = [
  { id: 'gmail',     label: 'Gmail',             icon: '✉️' },
  { id: 'indiamart', label: 'IndiaMart',         icon: '🛒' },
  { id: 'gallabox',  label: 'Gallabox WhatsApp', icon: '💬' },
  { id: 'call',      label: 'Phone Call',        icon: '📞' },
  { id: 'visit',     label: 'Field Visit',       icon: '🤝' },
];

const STATUSES = [
  { id: 'new',         label: 'New' },
  { id: 'contacted',   label: 'Contacted' },
  { id: 'interested',  label: 'Interested' },
  { id: 'negotiation', label: 'Negotiation' },
  { id: 'converted',   label: 'Converted ✓' },
  { id: 'lost',        label: 'Lost ✗' },
];

const fmtDate  = (ts) => { if (!ts) return '—'; return new Date(ts).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }); };
const todayStr = ()   => new Date().toISOString().split('T')[0];

// ─── Shared Styles ────────────────────────────────────────────
const COLORS = {
  surface: '#fbf9fa',
  surfaceContainer: '#efedee',
  surfaceContainerLow: '#f5f3f4',
  surfaceContainerLowest: '#ffffff',
  primary: '#bb0015',
  primaryContainer: '#e31f26',
  primaryFixed: '#ffdad6',
  onSurface: '#1b1c1c',
  onSurfaceVariant: '#5d3f3c',
  outlineVariant: '#e7bdb8',
  secondaryFixedDim: '#c8c6c7',
  errorContainer: '#ffdad6',
  successContainer: '#c6e5d9',
  onSuccessContainer: '#044c29',
};

const S = {
  page:       { minHeight:'100vh', background:COLORS.surface, fontFamily:"'Inter', sans-serif", color:COLORS.onSurface },
  card:       { background:COLORS.surfaceContainerLowest, borderRadius:6, padding: 24, boxShadow: '0 4px 40px rgba(27,28,28,0.02)' },
  label:      { display:'block', fontSize:12, fontWeight:600, color:COLORS.onSurfaceVariant, marginBottom:6, letterSpacing:'0.03em', textTransform:'uppercase' },
  input:      { width:'100%', padding:'12px 14px', borderRadius:2, border:'none', borderBottom:`2px solid ${COLORS.surfaceContainer}`, background:COLORS.surfaceContainerLow, color:COLORS.onSurface, fontSize:14, fontFamily:"'Inter', sans-serif", boxSizing:'border-box', outline:'none', transition:'border-color 0.2s' },
  btnPrimary: { padding:'12px 24px', borderRadius:6, border:'none', background:`linear-gradient(135deg, ${COLORS.primaryContainer}, ${COLORS.primary})`, color:'#fff', fontSize:14, fontWeight:600, cursor:'pointer', fontFamily:"'Inter', sans-serif", boxShadow:'0 4px 15px rgba(227,31,38,0.2)' },
  btnGhost:   { padding:'10px 16px', borderRadius:6, border:`1px solid rgba(231,189,184,0.3)`, background:'transparent', color:COLORS.primary, fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:"'Inter', sans-serif" },
  btnTertiary:{ background:'none', border:'none', color:COLORS.onSurface, fontSize:12, fontWeight:700, letterSpacing:'0.04em', textTransform:'uppercase', cursor:'pointer' }
};

// ─── App Root ─────────────────────────────────────────────────
export default function App() {
  const [session,     setSession]     = useState(undefined);
  const [profile,     setProfile]     = useState(null);
  const [leads,       setLeads]       = useState([]);
  const [profiles,    setProfiles]    = useState([]);
  const [view,        setView]        = useState('dashboard');
  const [toast,       setToast]       = useState(null);
  const [resetMode,   setResetMode]   = useState(false);

  const [leadModal,    setLeadModal]    = useState(null);
  const [detailLead,   setDetailLead]   = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [addUserModal, setAddUserModal] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const [fSrc,  setFSrc]  = useState('all');
  const [fSt,   setFSt]   = useState('all');
  const [fUser, setFUser] = useState('all');
  const [fQ,    setFQ]    = useState('');

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };

  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.includes('access_token') && hash.includes('type=recovery')) {
      setResetMode(true);
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) loadProfile(session.user.id);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setResetMode(true);
        setSession(session);
        return;
      }
      setSession(session);
      if (session) loadProfile(session.user.id);
      else { setProfile(null); setLeads([]); setProfiles([]); }
    });
    return () => subscription.unsubscribe();
  }, []);

  const loadProfile = async (uid) => {
    const { data } = await supabase.from('profiles').select('*').eq('id', uid).single();
    setProfile(data);
  };

  useEffect(() => {
    if (!profile) return;
    fetchLeads();

    const channel = supabase
      .channel('leads-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'leads' }, () => fetchLeads())
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [profile]);

  const fetchLeads = async () => {
    const { data } = await supabase
      .from('leads')
      .select('*')
      .order('created_at', { ascending: false });
    setLeads(data || []);
  };

  useEffect(() => {
    if (!profile) return;
    const loadProfiles = async () => {
      const { data } = await supabase.from('profiles').select('*').order('created_at');
      setProfiles(data || []);
    };
    loadProfiles();

    const channel = supabase
      .channel('profiles-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'profiles' }, loadProfiles)
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [profile]);

  const handleLogin = async (email, password) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setView('dashboard');
  };

  const saveLead = async (data) => {
    const payload = {
      name:           data.name,
      phone:          data.phone || null,
      email:          data.email || null,
      company:        data.company || null,
      source:         data.source,
      status:         data.status,
      assigned_to:    data.assignedTo || null,
      notes:          data.notes || null,
      value:          data.value || null,
      follow_up_date: data.followUpDate || null,
    };
    
    let dbRes;
    if (data.id) {
      dbRes = await supabase.from('leads').update(payload).eq('id', data.id);
    } else {
      dbRes = await supabase.from('leads').insert(payload);
    }

    if (dbRes.error) {
      console.error('Save error:', dbRes.error);
      showToast(dbRes.error.message || 'Error saving record', 'error');
      return;
    }

    showToast(data.id ? 'Lead updated' : 'Lead added');
    setLeadModal(null);
  };

  const deleteLead = async (id) => {
    const { error } = await supabase.from('leads').delete().eq('id', id);
    if (error) {
      showToast(error.message || 'Error deleting lead', 'error');
      return;
    }
    setDeleteTarget(null); setDetailLead(null);
    showToast('Lead deleted', 'error');
  };

  const updateLeadStatus = async (id, status) => {
    const { error } = await supabase.from('leads').update({ status }).eq('id', id);
    if (error) {
      showToast('Error updating status', 'error');
      throw error;
    }
    showToast(`Status updated to ${status}`);
    if (detailLead && detailLead.id === id) {
       setDetailLead(prev => ({ ...prev, status }));
    }
  };

  const updateLead = async (id, payload) => {
    const { error } = await supabase.from('leads').update(payload).eq('id', id);
    if (error) {
      showToast('Error updating lead record', 'error');
      throw error;
    }
    if (detailLead && detailLead.id === id) {
       setDetailLead(prev => ({ ...prev, ...payload }));
    }
  };

  const addUser = async ({ name, email, password, role }) => {
    const { data, error: authError } = await supabase.auth.signUp({ email, password });
    if (authError) {
      showToast(authError.message, 'error');
      return;
    }
    const { error: dbError } = await supabase.from('profiles').upsert({
      id:    data.user.id,
      name,
      email,
      role,
    });
    if (dbError) {
      showToast(dbError.message, 'error');
      return;
    }
    showToast('Team member added');
    setAddUserModal(false);
  };

  const deleteUserProfile = async (uid) => {
    const { error } = await supabase.from('profiles').delete().eq('id', uid);
    if (error) {
      showToast(error.message, 'error');
      return;
    }
    showToast('Member removed', 'error');
  };

  const mapLead = (l) => ({
    ...l,
    assignedTo:    l.assigned_to,
    followUpDate:  l.follow_up_date,
    createdAt:     l.created_at,
    updatedAt:     l.updated_at,
  });

  const uiLeads = leads.map(mapLead);

  const visibleLeads = uiLeads.filter(l => {
    if (fSrc  !== 'all' && l.source     !== fSrc)  return false;
    if (fSt   !== 'all' && l.status     !== fSt)   return false;
    if (fUser !== 'all' && l.assignedTo !== fUser)  return false;
    const q = fQ.toLowerCase();
    if (fQ && !l.name?.toLowerCase().includes(q) &&
              !l.company?.toLowerCase().includes(q) &&
              !String(l.value || '').toLowerCase().includes(q) &&
              !l.phone?.includes(fQ) &&
              !l.email?.toLowerCase().includes(q) &&
              !l.notes?.toLowerCase().includes(q) &&
              !l.source?.toLowerCase().includes(q) &&
              !l.status?.toLowerCase().includes(q)) return false;
    return true;
  });

  const stats = {
    total:     visibleLeads.length,
    new:       visibleLeads.filter(l => l.status === 'new').length,
    converted: visibleLeads.filter(l => l.status === 'converted').length,
    pipeline:  visibleLeads.filter(l => !['converted','lost'].includes(l.status)).length,
  };

  if (session === undefined) return (
    <div style={{ ...S.page, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ textAlign:'center', color:COLORS.onSurfaceVariant }}>
        <div style={{ fontSize:40, marginBottom:12 }}>⚡</div>
        <div style={{ fontSize:15 }}>Loading Precision CRM…</div>
      </div>
    </div>
  );

  if (resetMode) return (
    <ResetPasswordScreen onDone={() => { setResetMode(false); window.location.hash = ''; }} />
  );

  if (!session || !profile) return <LoginScreen onLogin={handleLogin} />;

  const allUsers = profiles.length > 0 ? profiles : [profile];

  return (
    <div style={S.page}>
      {toast && <Toast {...toast} />}
      <Sidebar view={view} setView={setView} user={profile} onLogout={handleLogout} />

      <div style={{ marginLeft:260, padding:'32px 64px 48px 48px', minHeight:'100vh', background:COLORS.surface }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:32 }}>
          <div style={{ display:'flex', alignItems:'center', gap:24, flex:1 }}>
             <h1 style={{ fontSize:28, fontWeight:800, margin:0, color:COLORS.onSurface, textTransform:'capitalize' }}>
                {view === 'dashboard' ? 'Dashboard' : view === 'leads' ? 'Leads' : view === 'team' ? 'Team' : view === 'reports' ? 'Reports' : view}
             </h1>
             {view === 'dashboard' && (
                  <div style={{ position:'relative', maxWidth:400, flex:1 }}>
                    <svg style={{ position:'absolute', left:14, top:12, color:COLORS.onSurfaceVariant }} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
                    <input value={fQ} onChange={(e) => { setFQ(e.target.value); }} style={{ width:'100%', padding:'10px 16px 10px 40px', borderRadius:20, border:'none', background:COLORS.surfaceContainerLow, color:COLORS.onSurface, fontSize:14, outline:'none', fontFamily:"'Inter', sans-serif" }} placeholder="Search leads by name, company, or offer no..." />
                  </div>
             )}
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:24 }}>
             <div style={{ position:'relative' }}>
               <div style={{ cursor:'pointer', width:36, height:36, borderRadius:18, background:COLORS.primary, color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:14 }} onClick={() => setUserMenuOpen(!userMenuOpen)}>
                 {profile?.name?.[0] || '?'}
               </div>
               {userMenuOpen && (
                 <>
                   <div style={{ position:'fixed', inset:0, zIndex:8000 }} onClick={() => setUserMenuOpen(false)} />
                   <div style={{ position:'absolute', top:'100%', right:0, marginTop:8, background:COLORS.surfaceContainerLowest, borderRadius:8, boxShadow:'0 10px 40px rgba(0,0,0,0.08)', padding:'8px 0', minWidth:160, zIndex:8001 }}>
                     <div style={{ padding:'8px 16px', fontSize:13, color:COLORS.onSurfaceVariant, borderBottom:`1px solid ${COLORS.surfaceContainerLow}`, marginBottom:8 }}>
                       <strong style={{ color:COLORS.onSurface }}>{profile?.name}</strong><br/>
                       {profile?.email}
                     </div>
                     <button onClick={() => { setUserMenuOpen(false); handleLogout(); }} style={{ width:'100%', textAlign:'left', padding:'10px 16px', background:'transparent', border:'none', cursor:'pointer', fontSize:14, color:COLORS.primary, fontWeight:600 }}>
                       Sign Out
                     </button>
                   </div>
                 </>
               )}
             </div>
          </div>
        </div>

        {view === 'dashboard' && (
          <Dashboard stats={stats} leads={visibleLeads} fQ={fQ} users={allUsers} user={profile} onAdd={() => setLeadModal('new')} />
        )}
        {view === 'leads' && !detailLead && (
          <LeadsView
            leads={visibleLeads} users={allUsers} user={profile}
            fSrc={fSrc} setFSrc={setFSrc} fSt={fSt} setFSt={setFSt}
            fUser={fUser} setFUser={setFUser} fQ={fQ} setFQ={setFQ}
            onAdd={() => setLeadModal('new')}
            onEdit={l => setLeadModal(l)}
            onView={l => setDetailLead(l)}
            onDelete={l => setDeleteTarget(l)}
          />
        )}
        {view === 'leads' && detailLead && (
          <LeadFullDetail 
            lead={uiLeads.find(l => l.id === detailLead.id) || detailLead} 
            users={allUsers} 
            onClose={() => setDetailLead(null)} 
            onEdit={() => setLeadModal(detailLead)} 
            onDelete={() => { setDetailLead(null); setDeleteTarget(detailLead); }} 
            onUpdateLead={updateLead}
          />
        )}
        {view === 'team' && profile.role === 'owner' && (
          <TeamView users={allUsers} leads={uiLeads}
            onAdd={() => setAddUserModal(true)} onDelete={deleteUserProfile} />
        )}
        {view === 'reports' && (
           <ReportsView leads={uiLeads} users={allUsers} />
        )}
      </div>

      {leadModal    && <LeadModal lead={leadModal === 'new' ? null : leadModal} users={allUsers} user={profile} onSave={saveLead} onClose={() => setLeadModal(null)} />}
      {deleteTarget && <DeleteConfirm lead={deleteTarget} onConfirm={() => deleteLead(deleteTarget.id)} onCancel={() => setDeleteTarget(null)} />}
      {addUserModal && <AddUserModal onSave={addUser} onClose={() => setAddUserModal(false)} />}
    </div>
  );
}

// ─── Toast ────────────────────────────────────────────────────
function Toast({ msg, type }) {
  return (
    <div style={{ position:'fixed', top:24, right:32, zIndex:9999, background:type==='error'?COLORS.primaryContainer:COLORS.onSurface, color:COLORS.surfaceContainerLowest, padding:'16px 24px', borderRadius:4, fontWeight:600, fontSize:14, boxShadow:'0 10px 40px rgba(0,0,0,0.1)' }}>
      {msg}
    </div>
  );
}

// ─── Shared Comps ─────────────────────────────────────────────
function PageHeader({ title, sub, action }) {
  return (
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', marginBottom:48 }}>
      <div>
        <div style={{ fontSize:40, fontWeight:700, letterSpacing:'-0.02em', color:COLORS.onSurface }}>{title}</div>
        {sub && <div style={{ fontSize:15, color:COLORS.onSurfaceVariant, marginTop:8 }}>{sub}</div>}
      </div>
      <div>{action}</div>
    </div>
  );
}

function Field({ label, children }) {
  return <div style={{ marginBottom:16 }}><label style={S.label}>{label}</label>{children}</div>;
}

function Sel({ value, onChange, opts }) {
  return (
    <select style={{ ...S.input, appearance:'none', cursor:'pointer' }} value={value} onChange={e => onChange(e.target.value)}>
      {opts.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
    </select>
  );
}

function Modal({ title, children, onClose, width=500 }) {
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(251,249,250,0.85)', backdropFilter:'blur(16px)', zIndex:9000, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:COLORS.surfaceContainerLowest, width, borderRadius:8, boxShadow:'0 20px 60px rgba(0,0,0,0.08)', overflow:'hidden' }}>
        <div style={{ padding:'32px 32px 24px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div style={{ fontSize:22, fontWeight:700 }}>{title}</div>
          <button onClick={onClose} style={{ background:'transparent', border:'none', fontSize:20, cursor:'pointer', color:COLORS.onSurfaceVariant }}>✕</button>
        </div>
        <div style={{ padding:'0 32px 32px', maxHeight:'75vh', overflowY:'auto' }}>{children}</div>
      </div>
    </div>
  );
}

function DeleteConfirm({ lead, onConfirm, onCancel }) {
  return (
    <Modal title="Delete Lead" onClose={onCancel} width={400}>
      <div style={{ fontSize:15, color:COLORS.onSurfaceVariant, marginBottom:24, lineHeight:1.5 }}>
        Are you sure you want to delete <strong style={{color:COLORS.onSurface}}>{lead.name}</strong>? This cannot be undone.
      </div>
      <div style={{ display:'flex', gap:12 }}>
        <button onClick={onCancel} style={{ ...S.btnGhost, flex:1, color:COLORS.onSurface }}>Cancel</button>
        <button onClick={onConfirm} style={{ ...S.btnPrimary, flex:1 }}>Delete</button>
      </div>
    </Modal>
  );
}

function AddUserModal({ onSave, onClose }) {
  const [form, setForm] = useState({ name:'', email:'', password:'', role:'sales' });
  const set = (k,v) => setForm(p => ({ ...p, [k]:v }));
  return (
    <Modal title="Add Team Member" onClose={onClose} width={400}>
      <Field label="Full Name"><input style={S.input} value={form.name} onChange={e=>set('name',e.target.value)} /></Field>
      <Field label="Email Address"><input style={S.input} type="email" value={form.email} onChange={e=>set('email',e.target.value)} /></Field>
      <Field label="Password"><input style={S.input} type="password" value={form.password} onChange={e=>set('password',e.target.value)} /></Field>
      <Field label="Role">
        <Sel value={form.role} onChange={v=>set('role',v)} opts={[{v:'sales',l:'Sales'},{v:'owner',l:'Owner'}]} />
      </Field>
      <div style={{ display:'flex', gap:12, marginTop:24 }}>
        <button onClick={onClose} style={{ ...S.btnGhost, flex:1, color:COLORS.onSurface }}>Cancel</button>
        <button onClick={() => { if(form.email) onSave(form); }} style={{ ...S.btnPrimary, flex:1 }}>Save Member</button>
      </div>
    </Modal>
  );
}


// ─── Login & Auth Screens ─────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [email, setEmail]         = useState('');
  const [password, setPassword]   = useState('');
  const [err, setErr]             = useState('');
  const [loading, setLoading]     = useState(false);
  const [showForgot, setShowForgot] = useState(false);

  const handle = async () => {
    if (!email || !password) { setErr('Please enter email and password'); return; }
    setLoading(true); setErr('');
    try { await onLogin(email.trim(), password); }
    catch { setErr('Invalid email or password'); }
    setLoading(false);
  };

  if (showForgot) return <ForgotPasswordScreen onBack={() => setShowForgot(false)} />;

  return (
    <div style={{ ...S.page, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ width:'100%', maxWidth:440 }}>
        <div style={{ textAlign:'center', marginBottom:48 }}>
          <div style={{ fontSize:40, fontWeight:800, color:COLORS.onSurface, letterSpacing:'-0.03em' }}>Precision CRM</div>
          <div style={{ fontSize:15, color:COLORS.onSurfaceVariant, marginTop:8, fontWeight:500 }}>The Atelier Dashboard</div>
        </div>
        <div style={{ ...S.card, padding:'40px', boxShadow:'0 10px 50px rgba(0,0,0,0.05)' }}>
          <div style={{ fontSize:22, fontWeight:700, color:COLORS.onSurface, marginBottom:32 }}>Authenticate</div>
          <div style={{ marginBottom:20 }}>
            <label style={S.label}>Email Address</label>
            <input style={S.input} type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" onKeyDown={e => e.key === 'Enter' && handle()} />
          </div>
          <div style={{ marginBottom:12 }}>
            <label style={S.label}>Password</label>
            <input style={S.input} type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" onKeyDown={e => e.key === 'Enter' && handle()} />
          </div>
          <div style={{ textAlign:'right', marginBottom:32 }}>
            <button onClick={() => setShowForgot(true)} style={S.btnTertiary}>Forgot Password?</button>
          </div>
          {err && <div style={{ background:COLORS.errorContainer, borderRadius:4, padding:'12px', color:COLORS.primary, fontSize:13, fontWeight:600, marginBottom:24 }}>⚠️ {err}</div>}
          <button onClick={handle} disabled={loading} style={{ ...S.btnPrimary, width:'100%', padding:'16px', fontSize:15, opacity:loading?0.7:1 }}>
            {loading ? 'Authenticating…' : 'Access Workspace'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ForgotPasswordScreen({ onBack }) {
  const [email, setEmail]     = useState('');
  const [sent, setSent]       = useState(false);
  const [err, setErr]         = useState('');
  const [loading, setLoading] = useState(false);

  const handle = async () => {
    if (!email) { setErr('Please enter your email'); return; }
    setLoading(true); setErr('');
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), { redirectTo: window.location.origin + '/' });
    if (error) { setErr(error.message); setLoading(false); return; }
    setSent(true); setLoading(false);
  };

  return (
    <div style={{ ...S.page, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ width:'100%', maxWidth:440 }}>
        <div style={{ ...S.card, padding:'40px' }}>
          {sent ? (
             <div style={{ textAlign:'center' }}>
               <div style={{ fontSize:40, marginBottom:24 }}>📧</div>
               <div style={{ fontSize:22, fontWeight:700, marginBottom:16 }}>Check Your Inbox</div>
               <div style={{ fontSize:15, color:COLORS.onSurfaceVariant, marginBottom:32, lineHeight:1.6 }}>Reset link dispatched to <br/><strong>{email}</strong></div>
               <button onClick={onBack} style={{ ...S.btnPrimary, width:'100%' }}>Return to Login</button>
             </div>
          ) : (
            <>
              <div style={{ fontSize:22, fontWeight:700, marginBottom:8 }}>Recover Access</div>
              <div style={{ fontSize:14, color:COLORS.onSurfaceVariant, marginBottom:32 }}>Provide your email to receive recovery instructions.</div>
              <div style={{ marginBottom:28 }}>
                <label style={S.label}>Email Address</label>
                <input style={S.input} type="email" value={email} onChange={e => setEmail(e.target.value)} onKeyDown={e => e.key === 'Enter' && handle()} />
              </div>
              {err && <div style={{ background:COLORS.errorContainer, borderRadius:4, padding:'12px', color:COLORS.primary, fontSize:13, fontWeight:600, marginBottom:24 }}>{err}</div>}
              <button onClick={handle} disabled={loading} style={{ ...S.btnPrimary, width:'100%', padding:'16px', marginBottom:16, opacity:loading?0.7:1 }}>
                {loading ? 'Dispatching…' : 'Send Reset Link'}
              </button>
              <button onClick={onBack} style={{ ...S.btnTertiary, width:'100%', textAlign:'center' }}>Return to Login</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ResetPasswordScreen({ onDone }) {
    return <div style={S.page}>... Reset Password Logic ...</div>;
}

// ─── Sidebar ──────────────────────────────────────────────────
function Sidebar({ view, setView, user, onLogout }) {
  const nav = [
    { id:'dashboard', label:'Dashboard', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"></rect><rect x="14" y="3" width="7" height="7"></rect><rect x="14" y="14" width="7" height="7"></rect><rect x="3" y="14" width="7" height="7"></rect></svg> },
    { id:'leads',     label:'Leads', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><polyline points="16 11 18 13 22 9"></polyline></svg> },
    ...(user?.role === 'owner' ? [
      { id:'team', label:'Team', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg> },
      { id:'reports', label:'Reports', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"></line><line x1="12" y1="20" x2="12" y2="4"></line><line x1="6" y1="20" x2="6" y2="14"></line></svg> }
    ] : [])
  ];
  return (
    <div style={{ position:'fixed', left:0, top:0, bottom:0, width:260, background:COLORS.surfaceContainerLow, display:'flex', flexDirection:'column', zIndex:100 }}>
      <div style={{ padding:'48px 32px 32px' }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <div style={{ background:COLORS.primary, borderRadius:8, width:32, height:32, display:'flex', alignItems:'center', justifyContent:'center' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path><polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline><line x1="12" y1="22.08" x2="12" y2="12"></line></svg>
          </div>
          <div>
            <div style={{ fontSize:18, fontWeight:800, color:COLORS.onSurface, letterSpacing:'-0.03em', lineHeight:1.2 }}>Lead Flow</div>
            <div style={{ fontSize:10, color:COLORS.onSurfaceVariant, fontWeight:700, letterSpacing:'0.05em' }}>ENTERPRISE EDITION</div>
          </div>
        </div>
      </div>
      <div style={{ padding:'0 16px', flex:1 }}>
        {nav.map(item => (
          <button key={item.id} onClick={() => setView(item.id)}
            style={{ width:'100%', display:'flex', alignItems:'center', gap:12, padding:'12px 16px', borderRadius:6, border:'none', cursor:'pointer', marginBottom:4, background:view===item.id?COLORS.surfaceContainerLowest:'transparent', color:view===item.id?COLORS.primary:COLORS.onSurfaceVariant, fontSize:14, fontWeight:600, fontFamily:"'Inter', sans-serif", textAlign:'left', boxShadow:view===item.id?'0 2px 8px rgba(0,0,0,0.02)':'none' }}>
            <span style={{ color:view===item.id?COLORS.primary:COLORS.onSurfaceVariant, display:'flex' }}>{item.icon}</span>
            {item.label}
            {view === item.id && <div style={{ position:'absolute', right:16, width:2, height:16, background:COLORS.primary, borderRadius:1 }} />}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Dashboard ────────────────────────────────────────────────
function Dashboard({ stats, leads, fQ, users, user, onAdd }) {
  const statusCounts = STATUSES.map(s => ({ ...s, count: leads.filter(l => l.status === s.id).length }));
  return (
    <div style={{ maxWidth: 1200 }}>
      <PageHeader title="Dashboard" sub="Performance & Pipeline Analytics" action={<button onClick={onAdd} style={S.btnPrimary}>+ Add Lead</button>} />

      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:24, marginBottom:48 }}>
        {[
          { label:'Total Volume',  value:stats.total },
          { label:'New Inquiries',    value:stats.new },
          { label:'Converted Deals',    value:stats.converted },
          { label:'Active Pipeline', value:stats.pipeline, color:COLORS.primary },
        ].map(c => (
          <div key={c.label} style={{ background:COLORS.surfaceContainerLowest, padding:'32px 24px', borderRadius:8 }}>
            <div style={{ fontSize:11, fontWeight:600, color:COLORS.onSurfaceVariant, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:12 }}>{c.label}</div>
            <div style={{ fontSize:32, fontWeight:700, color:c.color || COLORS.onSurface, letterSpacing:'-0.03em' }}>{c.value}</div>
          </div>
        ))}
      </div>

      <div style={{ display:'grid', gridTemplateColumns: fQ ? '1fr 1fr' : '2fr 1fr', gap:32 }}>
        <div>
          <div style={{ fontSize:18, fontWeight:700, marginBottom:24 }}>Pipeline Funnel</div>
          <div style={{ background:COLORS.surfaceContainerLowest, borderRadius:8, padding:32 }}>
            <div style={{ display:'flex', alignItems:'flex-end', height: 200, gap:12 }}>
                {statusCounts.map(s => {
                    const heightPct = stats.total ? (s.count / stats.total) * 100 : 0;
                    return (
                        <div key={s.id} style={{ flex:1, display:'flex', flexDirection:'column', justifyContent:'flex-end', alignItems:'center', height:'100%' }}>
                            <div style={{ width:'100%', background:s.id === 'converted' ? COLORS.onSurface : (s.id === 'new' ? COLORS.primary : COLORS.surfaceContainer), height: `${Math.max(10, heightPct)}%`, borderRadius:'4px 4px 0 0', transition:'height 0.3s' }} />
                            <div style={{ marginTop:16, fontSize:12, fontWeight:600, color:COLORS.onSurfaceVariant, textAlign:'center' }}>{s.label}<br/><span style={{color:COLORS.onSurface}}>{s.count}</span></div>
                        </div>
                    );
                })}
            </div>
          </div>
        </div>

        <div>
          <div style={{ fontSize:18, fontWeight:700, marginBottom:24 }}>
             {fQ ? 'Matching Results' : 'Priority Checklist'}
          </div>
          <div style={{ background:COLORS.surfaceContainerLowest, borderRadius:8, padding:24 }}>
            {(fQ ? leads : leads.filter(l => l.status === 'new')).slice(0, 10).map(l => (
              <div key={l.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'16px 0', borderBottom:`1px solid ${COLORS.surfaceContainerLow}` }}>
                 <div style={{ width:10, height:10, borderRadius:5, background: fQ ? COLORS.onSurfaceVariant : COLORS.primary }} />
                 <div style={{ flex:1 }}>
                   <div style={{ fontSize:14, fontWeight:700 }}>{l.name}</div>
                   <div style={{ fontSize:12, color:COLORS.onSurfaceVariant, marginTop:2 }}>{l.company || '—'} • {l.value || 'No Offer No'}</div>
                 </div>
                 <div style={{ fontSize:11, fontWeight:800, textTransform:'uppercase', color:COLORS.primary }}>{l.status}</div>
              </div>
            ))}
            {(fQ ? leads : leads.filter(l => l.status === 'new')).length === 0 && (
               <div style={{ padding:'24px', textAlign:'center', color:COLORS.onSurfaceVariant }}>
                 <div style={{ fontSize:24, marginBottom:8 }}>🔍</div>
                 <div style={{ fontSize:13 }}>No results found</div>
               </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── CRM Specific Component ───────────────────────────────────
function PulseBadge({ active }) {
    if (!active) return null;
    return (
        <span style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'4px 10px', borderRadius:20, background:COLORS.primaryFixed, color:COLORS.primary, fontSize:11, fontWeight:700, letterSpacing:'0.02em', textTransform:'uppercase' }}>
            <span style={{ width:6, height:6, borderRadius:3, background:COLORS.primary }} />
            Priority
        </span>
    );
}

// ─── Leads View ───────────────────────────────────────────────
function LeadsView({ leads, users, user, fSrc,setFSrc,fSt,setFSt,fUser,setFUser,fQ,setFQ, onAdd,onEdit,onView,onDelete }) {
  return (
    <div style={{ maxWidth: 1400 }}>
      <PageHeader title="Leads" sub={`${leads.length} records`} action={<button onClick={onAdd} style={S.btnPrimary}>+ Add Lead</button>} />
      
      <div style={{ display:'flex', gap:12, marginBottom:32, background:COLORS.surfaceContainerLowest, padding:16, borderRadius:8 }}>
        <input value={fQ} onChange={e => setFQ(e.target.value)} placeholder="Search records by name, company, or offer no..."
          style={{ ...S.input, flex:2, borderBottom:'none', background:'transparent', fontSize:15 }} />
        <Sel value={fSrc} onChange={setFSrc} opts={[{v:'all',l:'All Sources'},...SOURCES.map(s=>({v:s.id,l:s.label}))]} />
        <Sel value={fSt}  onChange={setFSt}  opts={[{v:'all',l:'All Statuses'},...STATUSES.map(s=>({v:s.id,l:s.label}))]} />
      </div>

      <div style={{ background:COLORS.surfaceContainerLowest, borderRadius:8, padding: '16px 0' }}>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead>
            <tr>
              {['Contact','Organization','Source','Status','Assignee','Offer No.','Action',''].map((h, i) => (
                <th key={i} style={{ padding:'16px 24px', textAlign:'left', fontSize:11, fontWeight:700, color:COLORS.onSurfaceVariant, textTransform:'uppercase', letterSpacing:'0.05em', borderBottom:`2px solid ${COLORS.surfaceContainerLow}` }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {leads.map(l => {
                const src = SOURCES.find(s => s.id === l.source);
                const st = STATUSES.find(s => s.id === l.status);
                const assignee = users.find(u => u.id === l.assignedTo);
                return (
                    <tr key={l.id} 
                        style={{ cursor:'pointer', transition:'background 0.2s' }}
                        onMouseEnter={e => e.currentTarget.style.background = COLORS.surfaceContainerLow}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        onClick={() => onView(l)}>
                        <td style={{ padding:'20px 24px' }}>
                            <div style={{ fontSize:15, fontWeight:600 }}>{l.name}</div>
                            {l.status === 'new' && <div style={{marginTop:6}}><PulseBadge active={true} /></div>}
                        </td>
                        <td style={{ padding:'20px 24px', fontSize:14, color:COLORS.onSurfaceVariant }}>{l.company || '—'}</td>
                        <td style={{ padding:'20px 24px', fontSize:13 }}>{src?.label}</td>
                        <td style={{ padding:'20px 24px' }}>
                            <span style={{ fontSize:12, fontWeight:600, color: st?.id === 'converted' ? COLORS.onSuccessContainer : COLORS.onSurface, background: st?.id === 'converted' ? COLORS.successContainer : COLORS.surfaceContainer, padding:'4px 10px', borderRadius:4 }}>
                                {st?.label}
                            </span>
                        </td>
                        <td style={{ padding:'20px 24px', fontSize:14 }}>{assignee?.name || 'Unassigned'}</td>
                        <td style={{ padding:'20px 24px', fontSize:14, fontWeight:600 }}>{l.value || '—'}</td>
                        <td style={{ padding:'20px 24px', fontSize:13, color:COLORS.onSurfaceVariant }}>{fmtDate(l.followUpDate)}</td>
                        <td style={{ padding:'20px 24px', textAlign:'right' }} onClick={e => e.stopPropagation()}>
                            <button onClick={() => onEdit(l)} style={{ ...S.btnGhost, padding:'6px 12px', marginRight:8 }}>Edit</button>
                        </td>
                    </tr>
                );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LeadModal({ lead, users, user, onSave, onClose }) {
  const [form, setForm] = useState({
    name:'', phone:'', email:'', company:'',
    source:'call', status:'new',
    assignedTo: user.role==='sales' ? user.id : (users[0]?.id||''),
    notes:'', value:'', followUpDate:'',
    ...(lead ? {
      ...lead,
      assignedTo: lead.assignedTo || lead.assigned_to || '',
      followUpDate: lead.followUpDate || lead.follow_up_date || '',
    } : {}),
  });
  const set = (k, v) => setForm(p => ({ ...p, [k]:v }));

  return (
    <Modal onClose={onClose} title={lead ? 'Edit Record' : 'New Lead Record'} width={640}>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:24, marginTop:16 }}>
        <Field label="Contact Name"><input style={S.input} value={form.name} onChange={e => set('name',e.target.value)} /></Field>
        <Field label="Organization"><input style={S.input} value={form.company} onChange={e => set('company',e.target.value)} /></Field>
        <Field label="Phone"><input style={S.input} value={form.phone} onChange={e => set('phone',e.target.value)} /></Field>
        <Field label="Email"><input style={S.input} value={form.email} onChange={e => set('email',e.target.value)} /></Field>
        
        <Field label="Inquiry Source">
          <Sel value={form.source} onChange={v=>set('source',v)} opts={SOURCES.map(s=>({v:s.id,l:s.label}))} />
        </Field>
        <Field label="Current Status">
           <Sel value={form.status} onChange={v=>set('status',v)} opts={STATUSES.map(s=>({v:s.id,l:s.label}))} />
        </Field>
        <Field label="Offer No.">
           <input style={S.input} type="text" value={form.value} onChange={e=>set('value',e.target.value)} />
        </Field>
        <Field label="Follow-Up Deadline">
           <input style={S.input} type="date" value={form.followUpDate} onChange={e=>set('followUpDate',e.target.value)} />
        </Field>
      </div>
      {user.role==='owner' && (
        <div style={{ marginTop:8 }}>
          <Field label="Appointed Assignee">
            <Sel value={form.assignedTo} onChange={v=>set('assignedTo',v)} opts={users.map(u=>({v:u.id,l:u.name}))} />
          </Field>
        </div>
      )}
      <div style={{ marginTop:8 }}>
        <Field label="Activity Ledger">
          <textarea style={{ ...S.input, height:100, resize:'vertical' }} value={form.notes} onChange={e=>set('notes',e.target.value)} />
        </Field>
      </div>
      <div style={{ display:'flex', gap:16, marginTop:32, justifyContent:'flex-end' }}>
        <button onClick={onClose} style={S.btnGhost}>Cancel</button>
        <button onClick={() => { if (!form.name.trim()) return; onSave(form); }} style={S.btnPrimary}>Commit Record</button>
      </div>
    </Modal>
  );
}

function LeadFullDetail({ lead, users, onClose, onEdit, onDelete, onUpdateLead }) {
  const [actText, setActText] = useState('');
  const src = SOURCES.find(s => s.id === lead.source);
  const st  = STATUSES.find(s => s.id === lead.status);
  
  return (
    <div style={{ maxWidth: 1200 }}>
       <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', display:'flex', alignItems:'center', gap:8, color:COLORS.onSurfaceVariant, fontWeight:600, fontSize:13, marginBottom:24, padding:0 }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline></svg> Back to Leads</button>

       <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:32 }}>
          <div style={{ display:'flex', alignItems:'center', gap:24 }}>
             <div style={{ width:72, height:72, borderRadius:8, background:COLORS.surfaceContainerLow, display:'flex', alignItems:'center', justifyContent:'center', fontSize:28, fontWeight:700, color:COLORS.onSurface }}>
               {lead.name[0]}
             </div>
             <div>
               <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:4 }}>
                  <div style={{ fontSize:28, fontWeight:800, color:COLORS.onSurface, letterSpacing:'-0.03em' }}>{lead.name}</div>
                  {lead.status === 'new' && <div style={{ background:COLORS.errorContainer, color:COLORS.primary, fontSize:10, fontWeight:800, padding:'4px 8px', borderRadius:20, letterSpacing:'0.05em' }}>HIGH PRIORITY</div>}
               </div>
               <div style={{ fontSize:15, color:COLORS.onSurfaceVariant, fontWeight:500 }}>Lead • <span style={{ color:COLORS.onSurface, fontWeight:700 }}>{lead.company || 'Unknown Organization'}</span></div>
             </div>
          </div>
          <div style={{ display:'flex', gap:12 }}>
             <button onClick={onEdit} style={S.btnGhost}>EDIT</button>
             {lead.status === 'negotiation' && (
               <button 
                 onClick={() => onUpdateLead(lead.id, { status: 'converted' })}
                 style={{ ...S.btnPrimary, background:COLORS.primaryContainer }}
               >
                 CONVERT
               </button>
             )}
          </div>
       </div>

       <div style={{ display:'grid', gridTemplateColumns:'320px 1fr', gap:32 }}>
          <div style={{ display:'flex', flexDirection:'column', gap:32 }}>
             <div style={{ background:COLORS.surfaceContainerLowest, borderRadius:8, padding:32, boxShadow:'0 10px 40px rgba(0,0,0,0.02)' }}>
                <div style={{ fontSize:10, fontWeight:700, color:COLORS.onSurfaceVariant, letterSpacing:'0.05em', marginBottom:24, textTransform:'uppercase' }}>Contact Intelligence</div>
                
                <div style={{ marginBottom:20 }}>
                   <div style={{ fontSize:11, fontWeight:600, color:COLORS.onSurfaceVariant, marginBottom:4 }}>DIRECT PHONE</div>
                   <div style={{ fontSize:14, fontWeight:600 }}>{lead.phone || '—'}</div>
                </div>
                <div style={{ marginBottom:20 }}>
                   <div style={{ fontSize:11, fontWeight:600, color:COLORS.onSurfaceVariant, marginBottom:4 }}>CORPORATE EMAIL</div>
                   <div style={{ fontSize:14, fontWeight:600 }}>{lead.email}</div>
                </div>
                <div>
                   <div style={{ fontSize:11, fontWeight:600, color:COLORS.onSurfaceVariant, marginBottom:4 }}>ACQUISITION SOURCE</div>
                   <div style={{ fontSize:14, fontWeight:600, display:'flex', alignItems:'center', gap:8 }}>{src?.icon} {src?.label}</div>
                </div>
             </div>

             <div style={{ background:COLORS.surfaceContainerLowest, borderRadius:8, padding:32, boxShadow:'0 10px 40px rgba(0,0,0,0.02)' }}>
                 <div style={{ fontSize:10, fontWeight:700, color:COLORS.onSurfaceVariant, letterSpacing:'0.05em', marginBottom:32, textTransform:'uppercase' }}>Deal Progression</div>
                 <div style={{ display:'flex', flexDirection:'column', gap:24, position:'relative' }}>
                    <div style={{ position:'absolute', left:10, top:10, bottom:10, width:2, background:COLORS.surfaceContainerLow, zIndex:0 }} />
                    {STATUSES.map((s, idx) => {
                       const stIdx = STATUSES.findIndex(x => x.id === lead.status);
                       const isChecked = idx <= stIdx;
                       const isNext = idx === stIdx + 1;
                       
                       let bgColor = 'transparent';
                       let borderColor = COLORS.surfaceContainerLow;
                       let textColor = COLORS.onSurfaceVariant;
                       let fontWeight = 600;

                       if (isChecked) {
                         bgColor = COLORS.primary;
                         borderColor = COLORS.primary;
                         textColor = COLORS.onSurface;
                         fontWeight = 700;
                       } else if (isNext) {
                         borderColor = COLORS.primary;
                         textColor = COLORS.onSurface;
                         fontWeight = 700;
                       }

                       return (
                         <div key={s.id} style={{ display:'flex', alignItems:'center', gap:16, zIndex:1 }}>
                           <div style={{ 
                             width:22, height:22, borderRadius:11, 
                             background: bgColor, 
                             border:`3px solid ${borderColor}`, 
                             display:'flex', alignItems:'center', justifyContent:'center', color:'#fff' 
                           }}>
                             {isChecked && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>}
                           </div>
                           <div style={{ fontSize:13, fontWeight:fontWeight, color:textColor }}>{s.label}</div>
                         </div>
                       )
                    })}
                 </div>
             </div>
          </div>

          <div style={{ display:'flex', flexDirection:'column', gap:32 }}>
              <div style={{ background:COLORS.surfaceContainerLowest, borderRadius:8, padding:32, boxShadow:'0 10px 40px rgba(0,0,0,0.02)' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:32 }}>
                      <div style={{ fontSize:10, fontWeight:700, color:COLORS.onSurfaceVariant, letterSpacing:'0.05em', textTransform:'uppercase' }}>Activity Ledger</div>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={COLORS.onSurfaceVariant} strokeWidth="2"><line x1="21" y1="10" x2="3" y2="10"></line><line x1="21" y1="6" x2="3" y2="6"></line><line x1="21" y1="14" x2="3" y2="14"></line><line x1="21" y1="18" x2="3" y2="18"></line></svg>
                  </div>
                  
                  <div style={{ display:'flex', flexDirection:'column', gap:40, marginBottom:40 }}>
                      {lead.notes && (
                         <div style={{ display:'grid', gridTemplateColumns:'min-content 1fr', gap:20 }}>
                            <div style={{ width:32, height:32, borderRadius:16, background:COLORS.surfaceContainerLow, display:'flex', alignItems:'center', justifyContent:'center', color:COLORS.onSurfaceVariant }}>
                               <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
                            </div>
                            <div>
                               <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:8 }}>
                                  <div style={{ fontSize:14, fontWeight:700 }}>Lead Record Update</div>
                                  <div style={{ fontSize:10, color:COLORS.onSurfaceVariant, letterSpacing:'0.05em' }}>FETCHED FROM RECORD</div>
                               </div>
                               <div style={{ fontSize:13, color:COLORS.onSurfaceVariant, lineHeight:1.6, whiteSpace:'pre-wrap' }}> {lead.notes}</div>
                            </div>
                         </div>
                      )}
                  </div>

                  <div style={{ background:COLORS.surfaceContainerLow, borderRadius:8, padding:16 }}>
                     <textarea 
                        placeholder="Type a note or log an activity..." 
                        style={{ width:'100%', border:'none', background:'transparent', fontSize:13, fontFamily:"'Inter',sans-serif", outline:'none', resize:'none', height:60 }}
                        value={actText}
                        onChange={(e) => setActText(e.target.value)}
                      ></textarea>
                     <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:16 }}>
                        <div style={{ display:'flex', gap:12, color:COLORS.onSurfaceVariant }}>
                           <button style={{ background:'none', border:'none', cursor:'pointer' }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path></svg></button>
                           <button style={{ background:'none', border:'none', cursor:'pointer', fontWeight:700 }}>@</button>
                        </div>
                        <button 
                           style={{ ...S.btnPrimary, padding:'8px 16px', fontSize:12 }}
                           onClick={() => {
                               if (!actText.trim()) return;
                               const timestamp = new Date().toLocaleString('en-US', {month:'short', day:'numeric', hour:'2-digit', minute:'2-digit'});
                               const newNotes = lead.notes ? `${lead.notes}\n\n[${timestamp}] ${actText}` : `[${timestamp}] ${actText}`;
                               onUpdateLead(lead.id, { notes: newNotes });
                               setActText('');
                           }}
                        >
                           Log Activity
                        </button>
                     </div>
                  </div>
              </div>
          </div>
       </div>
    </div>
  );
}

function TeamView({ users, leads, onAdd, onDelete }) {
  const totalLeads = leads.length;
  const assignedLeads = leads.filter(l => l.assignedTo).length;
  const assignmentPct = totalLeads ? ((assignedLeads / totalLeads) * 100).toFixed(1) : 0;

  // Mock activity pulse for design fidelity
  const pulse = [
    { id:1, name:'Julian Thorne', action:'moved 4 leads to', target:'Qualified', time:'2M AGO' },
    { id:2, name:'Sarah Chen', action:'updated the', target:'Quarterly Forecast', time:'1H AGO' },
    { id:3, name:'Elena Moretti', action:'closed lead', target:'#ABB-9021', time:'4H AGO' },
  ];

  return (
    <div style={{ maxWidth: 1200 }}>
       <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:40 }}>
          <h1 style={{ fontSize:28, fontWeight:800, margin:0, color:COLORS.onSurface }}>Team Directory</h1>
          <button onClick={onAdd} style={{ ...S.btnPrimary, padding:'12px 24px', fontSize:14, display:'flex', alignItems:'center', gap:8 }}>
             <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><line x1="19" y1="8" x2="19" y2="14"></line><line x1="16" y1="11" x2="22" y2="11"></line></svg>
             Invite Member
          </button>
       </div>

       {/* Dashboard Row */}
       <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:24, marginBottom:40 }}>
          <div style={{ ...S.card, display:'flex', justifyContent:'space-between', alignItems:'center', padding:'32px 40px' }}>
             <div>
                <div style={{ fontSize:11, fontWeight:700, color:COLORS.onSurfaceVariant, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:8 }}>Total Workspace Members</div>
                <div style={{ display:'flex', alignItems:'baseline', gap:24 }}>
                   <div style={{ fontSize:56, fontWeight:800, color:COLORS.onSurface }}>{users.length}</div>
                   {/* Avatar stack mockup */}
                   <div style={{ display:'flex', alignItems:'center' }}>
                      {[1,2,3].map(i => (
                         <div key={i} style={{ width:32, height:32, borderRadius:16, border:`3px solid ${COLORS.surfaceContainerLowest}`, background:COLORS.surfaceContainer, marginLeft: i===1?0:-12, display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:700 }}>USR</div>
                      ))}
                      <div style={{ fontSize:12, fontWeight:700, color:COLORS.onSurfaceVariant, marginLeft:12 }}>+{users.length - 3}</div>
                   </div>
                </div>
             </div>
          </div>
          <div style={{ ...S.card, background:COLORS.primary, color:'#fff', padding:'32px', position:'relative', overflow:'hidden' }}>
             <div style={{ position:'absolute', top:-20, right:-20, width:120, height:120, borderRadius:60, background:'rgba(255,255,255,0.05)' }} />
             <div style={{ fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:12, opacity:0.8 }}>Active Lead Assignment</div>
             <div style={{ fontSize:32, fontWeight:800, marginBottom:16 }}>{assignmentPct}%</div>
             <div style={{ height:6, background:'rgba(255,255,255,0.2)', borderRadius:3 }}>
                <div style={{ width:`${assignmentPct}%`, height:'100%', background:'#fff', borderRadius:3 }} />
             </div>
          </div>
       </div>

       {/* Team Grid */}
       <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:24, marginBottom:40 }}>
         {users.map(u => {
            const memberLeads = leads.filter(l => l.assignedTo === u.id);
            const activeDeals = memberLeads.filter(l => !['converted','lost'].includes(l.status)).length;
            const convertedCount = memberLeads.filter(l => l.status === 'converted').length;
            const score = memberLeads.length ? Math.round((convertedCount / memberLeads.length) * 100) : 0;

            return (
                <div key={u.id} style={{ ...S.card, padding:24, position:'relative' }}>
                    
                    <div style={{ width:56, height:56, borderRadius:4, background:COLORS.surfaceContainer, marginBottom:20, position:'relative', overflow:'hidden' }}>
                       <div style={{ width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, fontWeight:700, color:COLORS.onSurfaceVariant }}>{u.name[0]}</div>
                       {u.role === 'owner' && <div style={{ position:'absolute', bottom:2, right:2, width:6, height:6, borderRadius:3, background:COLORS.primary }} />}
                    </div>
                    
                    <div style={{ fontSize:18, fontWeight:700, color:COLORS.onSurface, marginBottom:4 }}>{u.name}</div>
                    <div style={{ fontSize:10, fontWeight:700, color:COLORS.onSurfaceVariant, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:32 }}>{u.role === 'owner' ? 'OWNER / DIRECTOR' : 'SALES REP'}</div>
                    
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:12 }}>
                       <div style={{ fontSize:10, fontWeight:700, color:COLORS.onSurfaceVariant, textTransform:'uppercase' }}>Active Deals</div>
                       <div style={{ fontSize:14, fontWeight:800 }}>{activeDeals}</div>
                    </div>
                    
                    <div>
                       <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:6 }}>
                          <div style={{ fontSize:10, fontWeight:700, color:COLORS.onSurfaceVariant, textTransform:'uppercase' }}>Performance Score</div>
                          <div style={{ fontSize:12, fontWeight:800 }}>{score}%</div>
                       </div>
                       <div style={{ height:4, background:COLORS.surfaceContainer, borderRadius:2 }}>
                          <div style={{ width:`${score}%`, height:'100%', background:COLORS.primary, borderRadius:2 }} />
                       </div>
                    </div>
                </div>
            )
         })}
         
         <div style={{ border:`2px dashed ${COLORS.surfaceContainer}`, borderRadius:6, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:280, cursor:'pointer' }} onClick={onAdd}>
            <div style={{ width:40, height:40, borderRadius:20, background:COLORS.surfaceContainerLow, display:'flex', alignItems:'center', justifyContent:'center', color:COLORS.onSurfaceVariant, marginBottom:16 }}>
               <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
            </div>
            <div style={{ fontSize:14, fontWeight:700, color:COLORS.onSurface }}>Invite Member</div>
            <div style={{ fontSize:11, color:COLORS.onSurfaceVariant, marginTop:4 }}>Add to your team</div>
         </div>
       </div>

    </div>
  );
}

function ReportsView({ leads, users }) {
  const statusStats = STATUSES.map(s => ({
    label: s.label,
    count: leads.filter(l => l.status === s.id).length
  }));

  const sourceStats = SOURCES.map(s => ({
    label: s.label,
    count: leads.filter(l => l.source === s.id).length
  }));

  const userStats = users.map(u => ({
    name: u.name,
    count: leads.filter(l => l.assignedTo === u.id).length,
    converted: leads.filter(l => l.assignedTo === u.id && l.status === 'converted').length
  })).sort((a, b) => b.count - a.count);

  const total = leads.length;
  const converted = leads.filter(l => l.status === 'converted').length;
  const convRate = total ? ((converted / total) * 100).toFixed(1) : 0;

  return (
    <div style={{ maxWidth: 1200 }}>
      <PageHeader title="Analytical Reports" sub="Real-time data insights" action={null} />

      {/* Summary Row */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:24, marginBottom:48 }}>
        <div style={S.card}>
            <div style={S.label}>Overall Conversion</div>
            <div style={{ fontSize:32, fontWeight:800, color:COLORS.primary }}>{convRate}%</div>
            <div style={{ fontSize:13, color:COLORS.onSurfaceVariant, marginTop:8 }}>{converted} out of {total} leads converted</div>
        </div>
        <div style={S.card}>
            <div style={S.label}>Total Active Leads</div>
            <div style={{ fontSize:32, fontWeight:800 }}>{leads.filter(l => !['converted','lost'].includes(l.status)).length}</div>
            <div style={{ fontSize:13, color:COLORS.onSurfaceVariant, marginTop:8 }}>Currently in pipeline</div>
        </div>
        <div style={S.card}>
            <div style={S.label}>Missing Offer Nos.</div>
            <div style={{ fontSize:32, fontWeight:800, color:COLORS.onSurfaceVariant }}>{leads.filter(l => !l.value).length}</div>
            <div style={{ fontSize:13, color:COLORS.onSurfaceVariant, marginTop:8 }}>Records requiring attention</div>
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:32, marginBottom:48 }}>
        {/* Status Distribution */}
        <div style={S.card}>
          <div style={{ fontSize:18, fontWeight:700, marginBottom:24 }}>Pipeline Distribution</div>
          <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
            {statusStats.map(s => {
              const pct = total ? (s.count / total) * 100 : 0;
              return (
                <div key={s.label}>
                  <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, fontWeight:600, marginBottom:8 }}>
                    <span>{s.label}</span>
                    <span>{s.count}</span>
                  </div>
                  <div style={{ height:8, background:COLORS.surfaceContainer, borderRadius:4, overflow:'hidden' }}>
                    <div style={{ width:`${pct}%`, height:'100%', background:COLORS.primary, borderRadius:4 }} />
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Source Distribution */}
        <div style={S.card}>
          <div style={{ fontSize:18, fontWeight:700, marginBottom:24 }}>Source Effectiveness</div>
          <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
            {sourceStats.map(s => {
              const pct = total ? (s.count / total) * 100 : 0;
              return (
                <div key={s.label}>
                  <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, fontWeight:600, marginBottom:8 }}>
                    <span>{s.label}</span>
                    <span>{s.count}</span>
                  </div>
                  <div style={{ height:8, background:COLORS.surfaceContainer, borderRadius:4, overflow:'hidden' }}>
                    <div style={{ width:`${pct}%`, height:'100%', background:COLORS.onSurface, borderRadius:4 }} />
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* Team Performance Table */}
      <div style={S.card}>
        <div style={{ fontSize:18, fontWeight:700, marginBottom:24 }}>Team Performance Summary</div>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead>
            <tr style={{ borderBottom:`1px solid ${COLORS.surfaceContainer}` }}>
              <th style={{ textAlign:'left', padding:'12px 8px', fontSize:11, color:COLORS.onSurfaceVariant, textTransform:'uppercase' }}>Team Member</th>
              <th style={{ textAlign:'center', padding:'12px 8px', fontSize:11, color:COLORS.onSurfaceVariant, textTransform:'uppercase' }}>Assignments</th>
              <th style={{ textAlign:'center', padding:'12px 8px', fontSize:11, color:COLORS.onSurfaceVariant, textTransform:'uppercase' }}>Converted</th>
              <th style={{ textAlign:'right', padding:'12px 8px', fontSize:11, color:COLORS.onSurfaceVariant, textTransform:'uppercase' }}>Success Rate</th>
            </tr>
          </thead>
          <tbody>
            {userStats.map(u => (
              <tr key={u.name} style={{ borderBottom:`1px solid ${COLORS.surfaceContainerLow}` }}>
                <td style={{ padding:'16px 8px', fontSize:14, fontWeight:600 }}>{u.name}</td>
                <td style={{ padding:'16px 8px', textAlign:'center', fontSize:14 }}>{u.count}</td>
                <td style={{ padding:'16px 8px', textAlign:'center', fontSize:14 }}>{u.converted}</td>
                <td style={{ padding:'16px 8px', textAlign:'right', fontSize:14, fontWeight:700, color:COLORS.primary }}>
                  {u.count ? ((u.converted / u.count) * 100).toFixed(0) : 0}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
